import { LoadingSpinner } from "components/LoadingSpinner";
import { orderStatusConstants, orderTypeConstants } from "constants/order";
import { distributionLogicValueList } from "constants/paymentPlanConstants";
import { mapPayerNameToCodeForDropdown } from "constants/practiceType";
import { ToastContext } from "context/ToastContext";
import dayjs from "dayjs";
import { parseDate, parseTime } from "helpers/date";
import { removeClearPatientFee } from "helpers/fee";
import { centsToDollars } from "helpers/price";
import useDocumentTitle from "hooks/useDocumentTitle";
import { usePayerInformation } from "hooks/usePayerInformation";
import { CLEAR_HEALTH_PAYER_NAME } from "hooks/useProviderProcedure";
import { useQuery } from "hooks/useQuery";
import { isEmpty } from "lodash";
import { useContext, useEffect, useMemo, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { RootState } from "store";
import { useGetAccountByIdQuery } from "store/queries/account";
import { useGetOrderQuery } from "store/queries/order";
import { useSearchProviderProcedureMutation } from "store/queries/providerProcedure";
import { onSelectDraftOrder } from "store/slices/order";
import {
  setAutoCompleteValue,
  setRows,
  setSelectedProcedures,
} from "store/slices/serviceDetails";
import { IAccountResponse } from "types/Account/AccountResponse";
import {
  IOrder,
  IGFEServiceDetails,
  IDefaultServiceDetails,
  IPatientResponsibilityServiceDetails,
} from "types/Order/Order";

import {
  VisitForm,
  Submit,
  PatientInfoForm,
  OrderType,
  ServiceDetails,
  Communication,
  PatientBenefitsSection,
} from "./components";
import { CancelReviewOrder } from "./components/CancelReviewOrder";
import { DraftHeader } from "./components/DraftHeader";
import { ErrorBanner } from "./components/ErrorBanner";
import { PaymentPlan } from "./components/PaymentPlan/PaymentPlan";
import { SaveDraft } from "./components/SaveDraft";
import {
  buildSelectedProceduresArray,
  buildSelectedProceduresObject,
  buildSelectedProceduresToTable,
} from "./components/ServiceDetails/builders";
import { formatPhoneNumberToMask } from "./utils/mask";

function ReviewDraftForm({
  draftOrder,
  accountData,
  isFetching,
}: {
  draftOrder: IOrder;
  accountData: IAccountResponse | undefined;
  isFetching: boolean;
}) {
  const dispatch = useDispatch();
  const [searchMutation] = useSearchProviderProcedureMutation();
  const [currentProviderProcedures, setCurrentProviderProcedures] =
    useState<any>([]);
  const {
    serviceDetails,
    patient,
    procedures,
    providerProcedures,
    amountInCents,
    accountNumber,
    dateOfService,
    orderType,
    accountId,
    gfeQuote,
    gfeQuoteId,
    id,
    paymentPlan,
    outreachStopped,
    account,
  } = draftOrder;

  const { integrationErrors } = useSelector(
    (state: RootState) => state.orderSlice
  );

  const estimatedVisitCost = useMemo(() => {
    if (orderType === orderTypeConstants.GFE) {
      return centsToDollars(
        (serviceDetails as IGFEServiceDetails)
          .patientResponsibilityEstimateAmountInCents || 0
      );
    }
    return centsToDollars(
      (serviceDetails as IDefaultServiceDetails).estimatedVisitCost || 0
    );
  }, [orderType, serviceDetails]);

  const { payerNameOptions } = usePayerInformation();

  const initialize = async () => {
    const autoCompleteValue = [
      ...new Set(
        serviceDetails.procedures.map(
          (procedure) =>
            `${procedure.code} - ${
              procedures?.find((p) => p?.cptCode === procedure.code.toString())
                ?.description
            }`
        )
      ),
    ];

    const selectedProcedureArrayWithDefaultDescriptions =
      buildSelectedProceduresArray({
        procedures: (serviceDetails as IGFEServiceDetails).procedures.map(
          (procedure) => {
            const defaultDescription = procedures?.find(
              (p) => p?.cptCode === procedure.code.toString()
            )?.description;
            return {
              code: procedure.code,
              providerProcedureId: procedure.providerProcedureId,
              specialty: procedure.specialty,
              cptDescription: defaultDescription || "",
            };
          }
        ),
      });

    const selectedProcedureArray = buildSelectedProceduresArray({
      procedures: (serviceDetails as IGFEServiceDetails).procedures.map(
        (procedure) => {
          return {
            code: procedure.code,
            providerProcedureId: procedure.providerProcedureId,
            specialty: procedure.specialty,
            cptDescription: procedure.cptDescription,
          };
        }
      ),
    });

    const selectedProceduresObject = buildSelectedProceduresObject(
      selectedProcedureArray
    );

    const rowsToSet = buildSelectedProceduresToTable({
      selectedProceduresObject,
      procedures: procedures || [],
      providerProcedures: providerProcedures || [],
      rows: [],
      // Don't pass originalProcedures here - we'll match after fetching all providers
    });

    const rowsWithoutDuplicateProviders = rowsToSet.map((row: any) => {
      const uniqueProviders = row.providerDropdown.filter(
        (provider: any, index: any, self: any) =>
          index === self.findIndex((p: any) => p.id === provider.id)
      );
      return {
        ...row,
        providerDropdown: uniqueProviders,
      };
    });

    // We'll set the correct amount after matching providers in rowsWithAllAvailableProviders
    const rowsWithCurrentAmount = rowsWithoutDuplicateProviders;

    const fetchedProviderProcedures: any[] = await Promise.all(
      selectedProceduresObject.map(async (procedure) => {
        const result = (await searchMutation({
          accountId,
          cptCode: procedure.code,
          payerName:
            orderType === orderTypeConstants.GFE
              ? serviceDetails.payerName
              : CLEAR_HEALTH_PAYER_NAME,
        })) as any;
        return result.data.data.data;
      })
    ).then((results) => results.flat());

    const rowsWithAllAvailableProviders = rowsWithCurrentAmount.map((row) => {
      const providerData = [...row.providerDropdown];
      fetchedProviderProcedures.forEach((providerProcedure) => {
        if (
          row?.cptCode === providerProcedure.procedure.cptCode &&
          row.specialty === providerProcedure.provider.specialty &&
          row.procedureId === providerProcedure.procedureId &&
          !providerData.find((p) => p.id === providerProcedure.provider.id)
        ) {
          providerData.push({
            id: providerProcedure.provider.id,
            name: providerProcedure.provider.name,
            specialty: providerProcedure.provider.specialty,
            priceInCents: providerProcedure.priceInCents,
          });
        }
      });

      // Now match the provider from the original order
      const originalProcedure = (
        serviceDetails as IGFEServiceDetails
      ).procedures.find(
        (p) => p.code === row.cptCode && p.specialty === row.specialty
      );

      let { selectedProvider } = row;
      let selectedAmount = row.amount;
      let selectedProviderProcedureId = row.providerProcedureId;

      if (originalProcedure?.providerId) {
        const matchingProvider = providerData.find(
          (p) => p.id === originalProcedure.providerId?.toString()
        );

        if (matchingProvider) {
          selectedProvider = matchingProvider;
          // Use the custom amount from the original order if it exists, otherwise use provider's default price
          selectedAmount =
            originalProcedure.amountInCents !== undefined &&
            originalProcedure.amountInCents !== null
              ? originalProcedure.amountInCents
              : matchingProvider.priceInCents;

          // Find the matching providerProcedure to get the correct providerProcedureId
          const matchingProviderProcedure = fetchedProviderProcedures.find(
            (pp: any) =>
              pp.providerId === originalProcedure.providerId?.toString() &&
              pp.provider.specialty === row.specialty &&
              pp.procedure.cptCode === row.cptCode
          );
          if (matchingProviderProcedure) {
            selectedProviderProcedureId = matchingProviderProcedure.id;
          }
        }
      }

      return {
        ...row,
        providerDropdown: providerData,
        selectedProvider,
        amount: selectedAmount,
        providerProcedureId: selectedProviderProcedureId,
      };
    });

    const rowsWithBenefitSelectionType = rowsWithAllAvailableProviders.map(
      (row) => {
        const procedure = serviceDetails.procedures.find(
          (p) =>
            p.code === row.cptCode &&
            p.specialty === row.specialty &&
            p.providerId === row.selectedProvider.id &&
            p.providerProcedureId === row.providerProcedureId
        );
        return {
          ...row,
          benefitSelectionType:
            procedure?.benefitsForProvProc?.benefitSelectionType,
        };
      }
    );

    dispatch(setRows(rowsWithBenefitSelectionType));
    dispatch(setAutoCompleteValue(autoCompleteValue));
    dispatch(
      setSelectedProcedures(selectedProcedureArrayWithDefaultDescriptions)
    );
    setCurrentProviderProcedures(fetchedProviderProcedures);
  };

  const payerName = useMemo(() => {
    if (orderType === orderTypeConstants.GFE) {
      return payerNameOptions.find(
        (aOption: { name: string; value: string }) =>
          aOption.name.toLowerCase() === draftOrder?.payer?.name?.toLowerCase()
      );
    }
    return null;
  }, [orderType, payerNameOptions, draftOrder?.payer?.name]);

  const payerName2 = useMemo(() => {
    if (orderType === orderTypeConstants.GFE) {
      return payerNameOptions.find(
        (aOption: { name: string; value: string }) =>
          aOption?.name?.toLowerCase() ===
          draftOrder?.secondaryPayer?.name?.toLowerCase()
      );
    }
    return null;
  }, [orderType, payerNameOptions, draftOrder?.secondaryPayer?.name]);

  const sendDefaultText = useMemo(() => {
    if (
      draftOrder.scheduledFirstCommunicationDate &&
      !draftOrder.scheduledFirstCommunicationSent &&
      draftOrder.scheduledCommunicationCustomText
    ) {
      return false;
    }
    return true;
  }, [draftOrder]);

  const paymentPlanActive = useMemo(
    () => paymentPlan.find((paymentPlan) => paymentPlan.active),
    [paymentPlan]
  );

  // Calculate downpayment only flat amount with clear patient fee adjustment
  const getDownpaymentOnlyFlatValue = useMemo(() => {
    if (
      paymentPlanActive?.paymentPlanMinDownPaymentFlat === null ||
      paymentPlanActive?.paymentPlanMinDownPaymentFlat === undefined
    ) {
      return null;
    }
    const valueInDollars = centsToDollars(
      paymentPlanActive.paymentPlanMinDownPaymentFlat
    );
    // Downgrade value if order has clear patient fee (for GFE orders)
    if (
      orderType === orderTypeConstants.GFE &&
      draftOrder.clearPatientFeeValueInPercentage
    ) {
      return removeClearPatientFee(
        valueInDollars,
        draftOrder.clearPatientFeeValueInPercentage
      );
    }
    return valueInDollars;
  }, [
    paymentPlanActive?.paymentPlanMinDownPaymentFlat,
    orderType,
    draftOrder.clearPatientFeeValueInPercentage,
  ]);

  const methods = useForm({
    defaultValues: {
      email: patient.email,
      amount: centsToDollars(
        (serviceDetails as IGFEServiceDetails)
          .patientResponsibilityEstimateAmountInCents || 0
      ),
      lastName: patient.lastName,
      firstName: patient.firstName,
      dateOfBirth: parseDate(patient.dateOfBirth),
      totalAmount: centsToDollars(amountInCents || 0),
      phoneNumber: patient.phoneNumber
        ? formatPhoneNumberToMask(patient.phoneNumber)
        : null,
      accountNumber,
      dateOfService: parseDate(dateOfService),
      timeOfService: parseTime(dateOfService),
      serviceDetails: serviceDetails.procedures,
      preferredContactMethod: patient.preferredContactMethod,
      estimatedVisitCostForPatient: estimatedVisitCost,
      orderType,
      accountId,
      payerName: payerName?.name || null,
      payerName2: payerName2?.name || null,
      payer: null,
      payer2: null,
      deductible: centsToDollars(gfeQuote?.remainingDeductibleInCents || 0),
      outOfPocketMax: centsToDollars(gfeQuote?.outOfPocketMaxInCents || 0),
      flatCopay: centsToDollars(gfeQuote?.flatCopayInCents || 0),
      professionalFlatCopay:
        draftOrder?.benefitDetails?.response?.professionalCopay ||
        centsToDollars(gfeQuote?.professionalFlatCopayInCents || 0),
      showProfessionalCopay:
        (draftOrder?.benefitDetails?.response?.professionalCopay || 0) > 0 ||
        (gfeQuote?.professionalFlatCopayInCents || 0) > 0,
      coinsurancePercent: gfeQuote?.coinsurancePercent || 0,
      deductible2: centsToDollars(
        gfeQuote?.secondaryPatientBenefits?.deductible || 0
      ),
      outOfPocketMax2: centsToDollars(
        gfeQuote?.secondaryPatientBenefits?.outOfPocketMax || 0
      ),
      flatCopay2: centsToDollars(
        gfeQuote?.secondaryPatientBenefits?.flatCopay || 0
      ),
      professionalFlatCopay2:
        draftOrder?.secondaryBenefitDetails?.response?.professionalCopay ||
        centsToDollars(
          gfeQuote?.secondaryPatientBenefits?.professionalFlatCopay || 0
        ),
      showProfessionalCopay2:
        (draftOrder?.secondaryBenefitDetails?.response?.professionalCopay ||
          0) > 0 ||
        (gfeQuote?.secondaryPatientBenefits?.professionalFlatCopay || 0) > 0,
      coinsurancePercent2:
        gfeQuote?.secondaryPatientBenefits?.coinsurancePercent || 0,
      gfeQuoteId,
      practiceType:
        draftOrder?.benefitDetails?.request?.practiceTypeCode ||
        mapPayerNameToCodeForDropdown[0]?.value,
      practiceType2:
        draftOrder?.secondaryBenefitDetails?.request?.practiceTypeCode ||
        mapPayerNameToCodeForDropdown[0]?.value,
      outOfPocketReason: serviceDetails?.outOfPocketReason
        ? { id: "", name: serviceDetails?.outOfPocketReason }
        : null,
      orderId: id,
      subscriberFirstName: patient.firstName,
      subscriberLastName: patient.lastName,
      subscriberDOB: parseDate(patient.dateOfBirth),
      memberId: patient?.patientBenefits?.memberID,
      memberId2: patient?.secondaryPatientBenefits?.memberID || null,
      isSubscriberPatient: patient?.patientBenefits?.isSubscriberPatient,
      paymentPlanDuration: paymentPlanActive?.paymentPlanDuration || null,
      paymentPlanDurationFull:
        (paymentPlanActive?.paymentPlanDuration || 0) + 1 || null,
      paymentPlanMinDownPaymentType:
        paymentPlanActive?.paymentPlanMinDownPaymentFlat !== null &&
        paymentPlanActive?.paymentPlanMinDownPaymentFlat !== undefined
          ? "Flat amount"
          : "Percentage",
      paymentPlanMinDownPaymentFlat:
        paymentPlanActive?.paymentPlanMinDownPaymentFlat !== null &&
        paymentPlanActive?.paymentPlanMinDownPaymentFlat !== undefined
          ? centsToDollars(paymentPlanActive.paymentPlanMinDownPaymentFlat)
          : null,
      paymentPlanMinDownPaymentPercent:
        paymentPlanActive?.paymentPlanMinDownPaymentPercent !== null
          ? paymentPlanActive?.paymentPlanMinDownPaymentPercent
          : null,
      paymentPlanEnabled: !!paymentPlanActive,
      draftOrderFirstRender: true,
      outreachEnabled: !outreachStopped,
      providerDistributionLogic:
        paymentPlanActive?.providerDistributionLogic ||
        account.providerDistributionLogic ||
        distributionLogicValueList.BY_PRIORITY,
      facilityCopay: !!gfeQuote?.costApplicationRules?.facilityRules?.useCopay,
      facilityDeductible:
        !!gfeQuote?.costApplicationRules?.facilityRules?.useDeductible,
      facilityCoinsurance:
        !!gfeQuote?.costApplicationRules?.facilityRules?.useCoinsurance,
      professionalCopay:
        !!gfeQuote?.costApplicationRules?.nonFacilityRules?.useCopay,
      professionalDeductible:
        !!gfeQuote?.costApplicationRules?.nonFacilityRules?.useDeductible,
      professionalCoinsurance:
        !!gfeQuote?.costApplicationRules?.nonFacilityRules?.useCoinsurance,
      secondaryInsuranceActivated: !!draftOrder?.secondaryPayerId,
      facilityCopay2:
        !!gfeQuote?.secondaryPatientBenefits?.costApplicationRules
          ?.facilityRules?.useCopay,
      facilityDeductible2:
        !!gfeQuote?.secondaryPatientBenefits?.costApplicationRules
          ?.facilityRules?.useDeductible,
      facilityCoinsurance2:
        !!gfeQuote?.secondaryPatientBenefits?.costApplicationRules
          ?.facilityRules?.useCoinsurance,
      professionalCopay2:
        !!gfeQuote?.secondaryPatientBenefits?.costApplicationRules
          ?.nonFacilityRules?.useCopay,
      professionalDeductible2:
        !!gfeQuote?.secondaryPatientBenefits?.costApplicationRules
          ?.nonFacilityRules?.useDeductible,
      professionalCoinsurance2:
        !!gfeQuote?.secondaryPatientBenefits?.costApplicationRules
          ?.nonFacilityRules?.useCoinsurance,
      benefitsUpdatedAt: gfeQuote?.benefitsUpdatedAt || null,
      secondaryBenefitsUpdatedAt:
        gfeQuote?.secondaryPatientBenefits?.benefitsUpdatedAt || null,
      benefitDetails: draftOrder?.benefitDetails || null,
      benefitDetails2: draftOrder?.secondaryBenefitDetails || null,

      ...(orderType === orderTypeConstants.PATIENT_RESPONSIBILITY && {
        totalInsuranceAllowable:
          (serviceDetails as IPatientResponsibilityServiceDetails)
            ?.totalInsuranceAllowableInCents === null
            ? null
            : centsToDollars(
                (serviceDetails as IPatientResponsibilityServiceDetails)
                  ?.totalInsuranceAllowableInCents || 0
              ),
      }),
      scheduledFirstCommunicationDate:
        draftOrder?.scheduledFirstCommunicationDate
          ? dayjs.utc(draftOrder.scheduledFirstCommunicationDate).toDate()
          : null,
      scheduledFirstCommunicationSent:
        draftOrder?.scheduledFirstCommunicationSent || false,
      scheduledCommunicationCustomText:
        draftOrder?.scheduledCommunicationCustomText || null,
      sendDefaultText,
      isDownPaymentOnly: paymentPlanActive?.isDownPaymentOnly || false,
      downpaymentOnlyType:
        paymentPlanActive?.paymentPlanMinDownPaymentFlat !== null &&
        paymentPlanActive?.paymentPlanMinDownPaymentFlat !== undefined
          ? "Flat amount"
          : "Percentage",
      downpaymentOnlyPercent:
        paymentPlanActive?.paymentPlanMinDownPaymentPercent !== null
          ? paymentPlanActive?.paymentPlanMinDownPaymentPercent
          : null,
      downpaymentOnlyFlatAmount: getDownpaymentOnlyFlatValue,
    },
  });

  const integrationErrorsParsed = useMemo(() => {
    return integrationErrors
      .filter((error: any) => error.active)
      .reduce((acc: any, error: any) => {
        acc[error.errorType] = error.message;
        return acc;
      }, {} as { [key: string]: string });
  }, [integrationErrors]);

  const selectedProceduresList = useMemo(() => {
    return serviceDetails.procedures.map(
      (procedure) => `${procedure.code} - ${procedure.cptDescription}`
    );
  }, [serviceDetails]);

  useEffect(() => {
    initialize();
  }, []);

  return (
    <FormProvider {...methods}>
      {!isEmpty(integrationErrorsParsed) && (
        <ErrorBanner integrationErrors={integrationErrorsParsed} />
      )}
      <DraftHeader />
      <OrderType
        orderTypesVisible={accountData?.data.orderTypesVisible}
        isFetching={isFetching}
      />
      <PatientInfoForm />
      <VisitForm />
      <PatientBenefitsSection
        draft
        account={account}
        costApplicationRules={gfeQuote?.costApplicationRules}
      />
      <ServiceDetails
        currentProviderProcedures={currentProviderProcedures}
        selectedProceduresList={selectedProceduresList}
        draft
      />
      {!!accountData?.data && (
        <PaymentPlan
          accountData={accountData}
          paymentPlan={paymentPlanActive}
          isDraft
        />
      )}
      {accountData?.data && <Communication account={accountData.data} draft />}
      <div className="flex flex-row">
        <div className="flex flex-1 justify-content-start align-items-start">
          <SaveDraft />
          <CancelReviewOrder orderId={id} />
        </div>
        <Submit draftOrder />
      </div>
    </FormProvider>
  );
}

export function ReviewOrder() {
  useDocumentTitle("Review Order");
  const { draftId } = useParams();
  const query = useQuery();
  const accountId = query.get("accountId") || "";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { data: order, error } = useGetOrderQuery(draftId);
  const { data: accountData, isFetching } = useGetAccountByIdQuery(accountId, {
    skip: isEmpty(accountId),
  });

  const { current: toastElement } = useContext(ToastContext);

  const draftOrder = useMemo(() => {
    return order?.data as IOrder;
  }, [order]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!isEmpty(draftOrder)) {
      if (
        (error as any)?.originalStatus === 404 ||
        draftOrder.status !== orderStatusConstants.DRAFT
      ) {
        toastElement?.show({
          summary: "Error",
          severity: "error",
          detail: "Draft order not found.",
        });
        navigate(`/hospitals/view/${accountId}`);
      } else {
        dispatch(onSelectDraftOrder(draftOrder as IOrder));
      }
    }
  }, [draftOrder]);

  if (isEmpty(draftOrder)) {
    return <LoadingSpinner />;
  }

  return (
    <ReviewDraftForm
      draftOrder={draftOrder}
      accountData={accountData}
      isFetching={isFetching}
    />
  );
}
